/*

path overview

/
├─ login
├─ users
└─ koumutens
    ├─ koumuten-1
    │   ├─ company-1
    │   │   ├─ models
    │   │   │   ├─ model-1
    │   │   │   │   ├─ labels
    │   │   │   │   └─ images
    │   │   │   │       ├─ image1
    │   │   │   │       ├─ image2
    │   │   │   │       └─ ...
    │   │   │   ├─ model-2
    │   │   │   └─ ...
    │   │   │
    │   │   └─ projects
    │   │       ├─ project-1
    │   │       │   └─ images
    │   │       │       ├─ image1
    │   │       │       ├─ image2
    │   │       │       └─ ...
    │   │       ├─ project-2
    │   │       └─ ...
    │   │
    │   ├─ company-2
    │   └─ ...
    │
    ├─ koumuten-2
    └─ ...
*/

export const pathnameElements = {
  koumutens: 'koumutens',
  companies: 'companies',
  images: 'images',
  labels: 'labels',
  login: 'login',
  models: 'models',
  projects: 'projects',
  summary: 'summary',
  users: 'users',
  csvs: 'csvs'
} as const;

/**
 * @description
 * react-routerのパラメータに使う文字列．
 * （例： `/companies/:companyId/`
 */
export type RouteParamElement =
  | 'koumutenId'
  | 'companyId'
  | 'modelId'
  | 'projectId'
  | 'imageId';

export type RouteParam = {
  koumutenId: string | undefined;
  companyId: string | undefined;
  modelId: string | undefined;
  projectId: string | undefined;
  imageId: string | undefined;
};

const routeParams: RouteParam = {
  koumutenId: 'koumutenId',
  companyId: 'companyId',
  modelId: 'modelId',
  projectId: 'projectId',
  imageId: 'imageId'
} as const;

const home = '/';
const login = `/${pathnameElements.login}`;

// /users
const userList = `/${pathnameElements.users}`;

// /csvs
const csvList = `/${pathnameElements.csvs}`;

// /koumutens
const koumutenList = `/${pathnameElements.koumutens}`;
const main = koumutenList;

// /koumutens/<koumuten-id>
const koumutenRoot = (koumutenId: number) => `${koumutenList}/${koumutenId}`;

// /koumutens/:koumutenId
const koumutenPlaceholder = `${koumutenList}/:${routeParams.koumutenId}`;

// /koumutens/<koumuten-id>/companies
const companyList = (koumutenId: number) =>
  `${koumutenRoot(koumutenId)}/${pathnameElements.companies}`;

// /koumutens/:koumutenId/companies
const companyListPlaceHolder = `${koumutenPlaceholder}/${pathnameElements.companies}`;

// /koumutens/<koumuten-id>/companies/<company-id>
const companyRoot = (koumutenId: number, companyId: number) =>
  `${companyList(koumutenId)}/${companyId}`;

// /koumutens/<koumuten-id>/companies/<company-id>
const companyPlaceholder = `${companyListPlaceHolder}/:${routeParams.companyId}`;

// /koumutens/<koumuten-id>/companies/<company-id>/projects
const projectList = (koumutenId: number, companyId: number) =>
  `${companyRoot(koumutenId, companyId)}/${pathnameElements.projects}`;

// /koumutens/:koumutenId/companies/:companyId/projects
const projectListPlaceholder = `${companyPlaceholder}/${pathnameElements.projects}`;

// /koumutens/<koumuten-id>/companies/<company-id>/projects/<project-id>
const projectRoot = (
  koumutenId: number,
  companyId: number,
  projectId: number
) => `${projectList(koumutenId, companyId)}/${projectId}`;

// /koumutens/:koumutenId/companies/:companyId/projects/:projectId
const projectRootPlaceholder = `${projectListPlaceholder}/:${routeParams.projectId}`;

// /koumutens/<koumuten-id>/companies/<company-id>/projects/<project-id>/images
const projectImageList = (
  koumutenId: number,
  companyId: number,
  projectId: number
) =>
  `${projectRoot(koumutenId, companyId, projectId)}/${pathnameElements.images}`;

// /koumutens/:koumutenId/companies/:companyId/projects/:projectId/images
const projectImageListPlaceholder = `${projectRootPlaceholder}/${pathnameElements.images}`;

// /koumutens/<koumuten-id>/companies/<company-id>/projects/<project-id>/images/<image-id>
const projectImageRoot = (
  koumutenId: number,
  companyId: number,
  projectId: number,
  imageId: number
) => `${projectImageList(koumutenId, companyId, projectId)}/${imageId}`;

// /koumutens/:koumutenId/companies/:companyId/projects/:projectId/images/:imageId
const projectImageRootPlaceholder = `${projectImageListPlaceholder}/:${routeParams.imageId}`;

// /koumutens/<koumuten-id>/companies/<company-id>/models
const modelList = (koumutenId: number, companyId: number) =>
  `${companyRoot(koumutenId, companyId)}/${pathnameElements.models}`;

// /koumutens/:koumutenId/companies/:companyId/models
const modelListPlaceholder = `${companyPlaceholder}/${pathnameElements.models}`;

// /koumutens/<koumuten-id>/companies/<company-id>/models/<model-id>
const modelRoot = (koumutenId: number, companyId: number, modelId: number) =>
  `${modelList(koumutenId, companyId)}/${modelId}`;

// /koumutens/:koumutenId/companies/:companyId/models/:modelId
const modelRootPlaceholder = `${modelListPlaceholder}/:${routeParams.modelId}`;

// /koumutens/<koumuten-id>/companies/<company-id>/models/<model-id>/labels
const labelListOfModel = (
  koumutenId: number,
  companyId: number,
  modelId: number
) => `${modelRoot(koumutenId, companyId, modelId)}/${pathnameElements.labels}`;

// /koumutens/:koumutenId/companies/:companyId/models/:modelId/labels
const labelListOfModelPlaceholder = `${modelRootPlaceholder}/${pathnameElements.labels}`;

// /koumutens/<koumuten-id>/companies/<company-id>/models/<model-id>/images
const modelImageList = (
  koumutenId: number,
  companyId: number,
  modelId: number
) => `${modelRoot(koumutenId, companyId, modelId)}/${pathnameElements.images}`;

// /koumutens/:koumutenId/companies/:companyId/models/:modelId/images
const modelImageListPlaceholder = `${modelRootPlaceholder}/${pathnameElements.images}`;

// /koumutens/<koumuten-id>/companies/<company-id>/models/<model-id>/images/<image-id>
const modelImageRoot = (
  koumutenId: number,
  companyId: number,
  modelId: number,
  imageId: number
) => `${modelImageList(koumutenId, companyId, modelId)}/${imageId}`;

// /koumutens/:koumutenId/companies/:companyId/models/:modelId/images/:imageId
const modelImageRootPlaceholder = `${modelImageListPlaceholder}/:${routeParams.imageId}`;

export const routes = {
  home,
  login,
  userList,
  csvList,
  koumutenList,
  koumutenPlaceholder,
  companyList,
  companyListPlaceHolder,
  main,
  companyRoot,
  companyPlaceholder,
  projectList,
  projectListPlaceholder,
  projectRootPlaceholder,
  projectRoot,
  projectImageList,
  projectImageListPlaceholder,
  projectImageRoot,
  projectImageRootPlaceholder,
  modelList,
  modelListPlaceholder,
  modelRootPlaceholder,
  modelRoot,
  labelListOfModel,
  labelListOfModelPlaceholder,
  modelImageList,
  modelImageListPlaceholder,
  modelImageRoot,
  modelImageRootPlaceholder
};
